import { Grid, ToggleButton, ToggleButtonGroup } from "@mui/material";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import { TemperatureField } from "components/LLMs/TemperatureField";
import type { LLMGenerationSettings } from "models/LLMs";
import React, { useMemo } from "react";

export type LLMGenerationMode = "temperature" | "thinking";

export interface LLMGenerationModel {
    name: string;
    isThinkingModel?: boolean;
    thinkingEffortLevels?: string[] | null;
}

type Option = {
    label: string;
    value: string;
};

type FieldLabelProps = {
    label: string;
    isDefault: boolean;
    onReset?: () => void;
};

interface Props {
    id: string;
    availableModels: LLMGenerationModel[];
    value?: LLMGenerationSettings | null;
    defaultValue?: LLMGenerationSettings | null;
    modelLabel?: string;
    temperatureLabel?: string;
    thinkingLabel?: string;
    modelPlaceholder?: string;
    isModelDefault?: boolean;
    isGenerationDefault?: boolean;
    isClearable?: boolean;
    onChange: (value: LLMGenerationSettings | null) => void;
    onModelReset?: () => void;
    onGenerationReset?: () => void;
    renderLabel?: (props: FieldLabelProps) => React.ReactNode;
}

export function getModelOptions(models: LLMGenerationModel[]): Option[] {
    return models.map((model) => ({
        label: model.name,
        value: model.name,
    }));
}

export function findAvailableModel(
    models: LLMGenerationModel[],
    modelName?: string | null,
) {
    if (!modelName) return undefined;
    return models.find(
        (model) => model.name.toLowerCase() === modelName.toLowerCase(),
    );
}

export function getThinkingOptions(model?: LLMGenerationModel): Option[] {
    if (model?.isThinkingModel !== true) return [];

    return (model.thinkingEffortLevels ?? []).map((level) => ({
        label: level,
        value: level,
    }));
}

export function getThinkingEffortLevelForModel(
    model?: LLMGenerationModel,
    currentThinkingEffortLevel?: string | null,
) {
    const thinkingOptions = getThinkingOptions(model);
    if (thinkingOptions.length === 0) return null;

    return (
        thinkingOptions.find(
            (option) => option.value === currentThinkingEffortLevel,
        )?.value ?? thinkingOptions[0].value
    );
}

export function getThinkingEffortLevelForModelName(
    models: LLMGenerationModel[],
    modelName?: string | null,
    currentThinkingEffortLevel?: string | null,
) {
    return getThinkingEffortLevelForModel(
        findAvailableModel(models, modelName),
        currentThinkingEffortLevel,
    );
}

export function getGenerationMode({
    model,
    thinkingEffortLevel,
    defaultThinkingEffortLevel,
    hasTemperatureOverride,
}: {
    model?: LLMGenerationModel;
    thinkingEffortLevel?: string | null;
    defaultThinkingEffortLevel?: string | null;
    hasTemperatureOverride: boolean;
}): LLMGenerationMode {
    if (getThinkingOptions(model).length === 0) return "temperature";
    if (thinkingEffortLevel) return "thinking";
    if (hasTemperatureOverride) return "temperature";
    return defaultThinkingEffortLevel ? "thinking" : "temperature";
}

export function coerceTemperature(value: string | number | null | undefined) {
    const num = typeof value === "number" ? value : parseFloat(value ?? "");
    if (!Number.isFinite(num)) return null;
    return Math.min(Math.max(num, 0), 2);
}

function defaultRenderLabel({ label }: FieldLabelProps) {
    return label;
}

export function LLMModelGenerationSettings({
    id,
    availableModels,
    value,
    defaultValue,
    modelLabel = "LLM Model",
    temperatureLabel = "Temperature",
    thinkingLabel = "Thinking Level",
    modelPlaceholder = "Select model...",
    isModelDefault,
    isGenerationDefault,
    isClearable = true,
    onChange,
    onModelReset,
    onGenerationReset,
    renderLabel = defaultRenderLabel,
}: Props) {
    const modelOptions = useMemo(
        () => getModelOptions(availableModels),
        [availableModels],
    );
    const effectiveModelName = value?.modelName ?? defaultValue?.modelName;
    const selectedModel = findAvailableModel(availableModels, effectiveModelName);
    const thinkingOptions = useMemo(
        () => getThinkingOptions(selectedModel),
        [selectedModel],
    );
    const canUseThinking = thinkingOptions.length > 0;
    const temperatureOverrideSelected = value?.temperature != null;
    const mode = getGenerationMode({
        model: selectedModel,
        thinkingEffortLevel: value?.thinkingEffortLevel,
        defaultThinkingEffortLevel: defaultValue?.thinkingEffortLevel,
        hasTemperatureOverride: temperatureOverrideSelected,
    });
    const selectedThinkingLevel =
        value?.thinkingEffortLevel ?? defaultValue?.thinkingEffortLevel;
    const selectedThinkingOption =
        thinkingOptions.find(
            (option) => option.value === selectedThinkingLevel,
        ) ?? thinkingOptions[0];
    const selectedModelOption = effectiveModelName
        ? { label: effectiveModelName, value: effectiveModelName }
        : undefined;
    const resetModel =
        onModelReset ??
        (() =>
            onChange({
                modelName: null,
                temperature: value?.temperature ?? null,
                thinkingEffortLevel: null,
            }));
    const resetGeneration =
        onGenerationReset ??
        (() =>
            onChange(
                value?.modelName ? { modelName: value.modelName } : null,
            ));

    const updateGeneration = (patch: Partial<LLMGenerationSettings>) => {
        onChange({
            modelName: value?.modelName ?? null,
            ...patch,
        });
    };

    return (
        <Grid container direction="column" rowGap={1}>
            <Grid item>
                {renderLabel({
                    label: modelLabel,
                    isDefault: isModelDefault ?? (value?.modelName == null),
                    onReset: resetModel,
                })}
                <AcxSelect
                    key={`${id}-model-${effectiveModelName ?? ""}`}
                    id={`${id}-model`}
                    options={modelOptions}
                    isClearable={isClearable}
                    defaultValue={selectedModelOption}
                    placeholder={modelPlaceholder}
                    onChange={(opt: { value: string } | null) => {
                        if (!opt) {
                            onChange(null);
                            return;
                        }

                        const model = findAvailableModel(
                            availableModels,
                            opt.value,
                        );
                        const thinkingEffortLevel = getThinkingEffortLevelForModel(
                            model,
                            value?.thinkingEffortLevel ??
                                defaultValue?.thinkingEffortLevel,
                        );

                        if (thinkingEffortLevel) {
                            onChange({
                                modelName: opt.value,
                                thinkingEffortLevel,
                                temperature: null,
                            });
                            return;
                        }

                        onChange({
                            modelName: opt.value,
                            temperature:
                                value?.temperature ??
                                defaultValue?.temperature ??
                                0,
                            thinkingEffortLevel: null,
                        });
                    }}
                />
            </Grid>
            {canUseThinking && (
                <Grid item>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={mode}
                        onChange={(_, nextMode: LLMGenerationMode | null) => {
                            if (!nextMode) return;
                            if (nextMode === "temperature") {
                                updateGeneration({
                                    temperature:
                                        value?.temperature ??
                                        defaultValue?.temperature ??
                                        0,
                                    thinkingEffortLevel: null,
                                });
                                return;
                            }

                            updateGeneration({
                                temperature: null,
                                thinkingEffortLevel:
                                    selectedThinkingOption?.value ??
                                    thinkingOptions[0]?.value ??
                                    null,
                            });
                        }}
                    >
                        <ToggleButton value="temperature">
                            Temperature
                        </ToggleButton>
                        <ToggleButton value="thinking">Thinking</ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            )}
            {mode === "thinking" && canUseThinking ? (
                <Grid item>
                    {renderLabel({
                        label: thinkingLabel,
                        isDefault:
                            isGenerationDefault ??
                            (value?.thinkingEffortLevel == null),
                        onReset: resetGeneration,
                    })}
                    <AcxSelect
                        key={`${id}-thinking-${selectedThinkingOption?.value ?? ""}`}
                        id={`${id}-thinking`}
                        options={thinkingOptions}
                        defaultValue={selectedThinkingOption}
                        onChange={(opt: { value: string } | undefined) =>
                            updateGeneration({
                                temperature: null,
                                thinkingEffortLevel: opt?.value ?? null,
                            })
                        }
                    />
                </Grid>
            ) : (
                <Grid item>
                    {renderLabel({
                        label: temperatureLabel,
                        isDefault:
                            isGenerationDefault ?? (value?.temperature == null),
                        onReset: resetGeneration,
                    })}
                    <TemperatureField
                        id={`${id}-temperature`}
                        value={value?.temperature ?? null}
                        placeholder={defaultValue?.temperature ?? null}
                        onChange={(temperature) =>
                            updateGeneration({
                                temperature,
                                thinkingEffortLevel: null,
                            })
                        }
                    />
                </Grid>
            )}
        </Grid>
    );
}
